// Iter 359 — Komponen Riwayat Pekerjaan yang reusable.
//   Dipakai oleh Mockup Shopee (shopee_module.js) & Mockup Offline (production_queue.js).
//   Perbedaan sumber data (Shopee: shopee_jobs; Offline: orders.products) ditangani
//   lewat NORMALISASI data ke bentuk `item` di sisi pemanggil, bukan dalam komponen.
//   Komponen ini READ-ONLY & tidak mengubah workflow produksi.

import { useEffect, useMemo, useState } from "react";
import {
  Search, Filter, Calendar, Users as UsersIcon, History, Ban,
  CheckCircle2, PenSquare, Warehouse as WarehouseIcon, X,
  ChevronRight, User as UserIcon, Image as ImgIcon,
} from "lucide-react";
import { JenisPengerjaanBadge } from "./jenis_pengerjaan_badge";
import { HoldKebabMenu } from "./hold_cancel_panel";

// Status map universal untuk pola Mockup (BELUM_FIX / REVISI / FIX / BATAL).
const STATUS_META = {
  BELUM_FIX: { t: "Belum Fix", bg: "#fef3c7", c: "#92400e" },
  REVISI:    { t: "Revisi",    bg: "#fde68a", c: "#92400e" },
  FIX:       { t: "Fix",       bg: "#dcfce7", c: "#166534" },
  BATAL:     { t: "Batal",     bg: "#fee2e2", c: "#991b1b" },
};

export const SENT_24H = "SENT_24H";

export function RiwayatPekerjaanBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.BELUM_FIX;
  // Iter 362 — Inline style agar badge selalu rounded (pill) & konsisten baik
  //   di dalam modal maupun di card inline (tab Riwayat Mia).
  return <span className="riw-status-badge" style={{
    background: m.bg, color: m.c,
    padding: "3px 10px", borderRadius: 999,
    fontSize: 11.5, fontWeight: 700, lineHeight: 1.2,
    display: "inline-flex", alignItems: "center", gap: 4,
  }}>{m.t}</span>;
}

/**
 * @param items[]        — array ter-normalisasi, shape per item:
 *   { id, order_number, customer, pic_name, job_label, status (BELUM_FIX|REVISI|FIX|BATAL),
 *     created_at, updated_at, revision_count, is_fix, next_label, next_cls,
 *     is_cancelled, cancel_reason, extra_label (opsional: "Lembur" dll) }
 * @param pics[]          — opsi filter PIC: [{id,name}] (opsional)
 * @param canFilterByPic  — tampilkan dropdown PIC
 * @param title           — default "Riwayat Pekerjaan"
 * @param onOpen(item)    — klik kartu (opsional). Jika null, kartu tidak clickable.
 * @param onClose         — tutup modal.
 * @param me              — {name} opsional untuk ringkasan.
 * @param flavor          — "shopee" (orange) | "offline" (teal) — kelas warna wrapper.
 * @param testPrefix      — prefix data-testid, default "riwayat-pekerjaan".
 * @param inline          — bila true, render tanpa overlay/modal (dipakai di dalam tab).
 */
export function RiwayatPekerjaanModal({
  items,
  pics,
  canFilterByPic = false,
  title = "Riwayat Pekerjaan",
  onOpen,
  onClose,
  me,
  flavor = "offline",
  testPrefix = "riwayat-pekerjaan",
  inline = false,
  initialStatus = "",
  onHoldDone,
}) {
  const [q, setQ] = useState("");
  const [picId, setPicId] = useState("");
  const [status, setStatus] = useState(initialStatus);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fmtDT = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("id-ID", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta",
      });
    } catch { return "—"; }
  };

  const filtered = useMemo(() => {
    const sent24 = status === SENT_24H;
    const sentMs = (it) => it.sent_at ? new Date(it.sent_at).getTime() : 0;
    const cutoff = Date.now() - 24 * 3600 * 1000;
    const qnorm = (q || "").trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
    const base = (items || []).filter(it => {
      if (sent24) {
        // Benar-benar di Gudang (bukan ditarik) & dikirim ≤24 jam terakhir.
        const ws = it._raw?.warehouse_state;
        if (ws && !["sent", "completed", "on_hold"].includes(ws)) return false;
        if (!(sentMs(it) >= cutoff)) return false;
      } else if (status && it.status !== status) return false;
      if (picId && (it.pic_id || "") !== picId) return false;
      if (qnorm) {
        const hay = `${it.order_number || ""} ${it.customer || ""} ${it.job_label || ""}`.toLowerCase();
        if (!hay.includes(qnorm)) return false;
      }
      if (from || to) {
        const t = it.created_at ? new Date(it.created_at).getTime() : 0;
        if (from && t < from) return false;
        if (to && t > to) return false;
      }
      return true;
    });
    if (sent24) return base.sort((a, b) => sentMs(b) - sentMs(a));
    return base.sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [items, q, picId, status, dateFrom, dateTo]);

  const filtersActive = Boolean(q || picId || status || dateFrom || dateTo);
  const clearFilters = () => {
    setQ(""); setPicId(""); setStatus(""); setDateFrom(""); setDateTo("");
  };

  const palette = flavor === "shopee"
    ? { card: "#fffbeb", cardBorder: "#fed7aa", hover: "#ea580c", accent: "#9a3412", chipRev: { bg: "#fde68a", c: "#92400e" } }
    : { card: "#f0fdfa", cardBorder: "#99f6e4", hover: "#0d9488", accent: "#115e59", chipRev: { bg: "#fef3c7", c: "#92400e" } };

  // Iter 360 — Mode inline (dipakai di dalam tab) vs modal overlay.
  //   Inline: tanpa fixed overlay, tanpa tombol Tutup; parent tab yang mengatur.
  const Wrapper = ({ children }) => inline ? (
    <div className={`riw-inline riw-flavor-${flavor}`} data-testid={`${testPrefix}-inline`}>{children}</div>
  ) : (
    <div className={`riw-modal-overlay riw-flavor-${flavor}`} data-testid={`${testPrefix}-modal`}
      onClick={e => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>{children}</div>
  );

  const Inner = ({ children }) => inline
    ? <div className="riw-inline-body">{children}</div>
    : <div className="riw-modal-card">{children}</div>;

  return <Wrapper>
    <style>{`
      .riw-modal-overlay .riw-modal-card, .riw-inline .riw-inline-body{background:#fff;max-width:960px;width:100%;max-height:92vh;overflow:auto;border-radius:14px;padding:16px;box-shadow:0 10px 28px rgba(0,0,0,.3);}
      .riw-inline .riw-inline-body{box-shadow:none;max-height:none;padding:0;background:transparent;}
      .riw-modal-overlay .riw-modal-head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:12px;}
      .riw-modal-overlay .riw-modal-head h3{margin:0;font-size:16px;font-weight:700;display:inline-flex;align-items:center;gap:8px;}
      .riw-modal-overlay .riw-close-btn{background:#f1f5f9;color:#0f172a;border:0;border-radius:8px;padding:6px 12px;font-size:13px;font-weight:600;cursor:pointer;min-height:36px;}
      .riw-modal-overlay .riw-close-btn:hover{background:#e2e8f0;}
      .riw-filter{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:10px;align-items:center;}
      .riw-filter .riw-field{display:flex;align-items:center;gap:6px;background:${palette.card};border:1px solid ${palette.cardBorder};border-radius:8px;padding:6px 10px;min-height:36px;flex:1 1 200px;min-width:0;}
      .riw-filter .riw-field input, .riw-filter .riw-field select{border:0;outline:0;background:transparent;font-size:13px;flex:1;min-width:0;color:#0f172a;}
      .riw-clear{background:transparent;border:1px solid #fca5a5;color:#b91c1c;border-radius:8px;padding:6px 10px;font-size:12.5px;font-weight:600;cursor:pointer;min-height:36px;}
      .riw-clear:hover{background:#fee2e2;}
      .riw-count{font-size:12px;color:#64748b;padding:4px 0 6px;}
      .riw-count b{color:#0f172a;}
      .riw-list{display:flex;flex-direction:column;gap:8px;}
      /* Iter 381 — Cegah horizontal overflow di mobile untuk setiap kartu riwayat. */
      .riw-card{background:${palette.card};border:1px solid ${palette.cardBorder};border-radius:12px;padding:10px 12px;transition:border-color .15s ease, box-shadow .15s ease;${onOpen ? "cursor:pointer;" : ""}max-width:100%;min-width:0;box-sizing:border-box;overflow:hidden;}
      ${onOpen ? `.riw-card:hover{border-color:${palette.hover};box-shadow:0 2px 8px rgba(0,0,0,.08);}` : ""}
      /* Iter 470 — Thumbnail + grid layout: thumb (72px) di kiri, konten kanan. */
      .riw-card-body{display:grid;grid-template-columns:72px 1fr;gap:12px;align-items:start;min-width:0;}
      .riw-card-thumb{position:relative;width:72px;height:72px;border-radius:10px;overflow:hidden;border:1px solid ${palette.cardBorder};background:#f8fafc;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
      .riw-card-thumb img{width:100%;height:100%;object-fit:cover;display:block;transition:opacity .18s ease;}
      .riw-card-thumb-ph{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;padding:4px;color:#94a3b8;font-size:10px;font-weight:600;text-align:center;line-height:1.2;}
      .riw-card-main{min-width:0;display:flex;flex-direction:column;gap:3px;}
      .riw-card-head{display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;margin-bottom:2px;min-width:0;}
      .riw-card-id{font-family:'Space Mono',monospace;font-size:13px;font-weight:700;color:${palette.accent};overflow-wrap:anywhere;word-break:break-word;min-width:0;max-width:100%;}
      .riw-status-badge{padding:3px 8px;border-radius:6px;font-size:11.5px;font-weight:700;display:inline-flex;align-items:center;gap:4px;flex-shrink:0;}
      .riw-card-title{font-size:14px;font-weight:700;color:#1c1917;margin:0;overflow-wrap:anywhere;word-break:break-word;line-height:1.3;}
      .riw-card-sub{font-size:12px;color:#64748b;margin:2px 0 4px;overflow-wrap:anywhere;word-break:break-word;line-height:1.3;}
      .riw-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:4px;}
      .riw-chip{display:inline-flex;align-items:center;gap:4px;font-size:11.5px;font-weight:600;padding:3px 8px;border-radius:999px;background:#e2e8f0;color:#334155;}
      .riw-chip-success{background:#dcfce7;color:#166534;}
      .riw-chip-warn{background:${palette.chipRev.bg};color:${palette.chipRev.c};}
      .riw-chip-danger{background:#fee2e2;color:#991b1b;}
      .riw-chip-neutral{background:#e2e8f0;color:#334155;}
      .riw-meta{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:11.5px;color:#44403c;margin-top:5px;min-width:0;}
      .riw-meta b{color:#1c1917;font-weight:700;overflow-wrap:anywhere;word-break:break-word;}
      .riw-empty{text-align:center;padding:24px 12px;color:#64748b;font-size:13px;background:${palette.card};border:1px dashed ${palette.cardBorder};border-radius:10px;}

      /* ============ Iter 470 — Dark mode override lengkap ============ */
      :root[data-theme="dark"] .riw-modal-overlay .riw-modal-card,
      :root[data-theme="dark"] .riw-inline .riw-inline-body{background:#15181f;color:#e2e8f0;}
      :root[data-theme="dark"] .riw-inline .riw-inline-body{background:transparent;}
      :root[data-theme="dark"] .riw-modal-overlay .riw-modal-head h3{color:#f1f5f9;}
      :root[data-theme="dark"] .riw-modal-overlay .riw-close-btn{background:#273040;color:#e2e8f0;}
      :root[data-theme="dark"] .riw-modal-overlay .riw-close-btn:hover{background:#334155;}
      :root[data-theme="dark"] .riw-filter .riw-field{background:#1a1d26;border-color:#2f333e;}
      :root[data-theme="dark"] .riw-filter .riw-field input,
      :root[data-theme="dark"] .riw-filter .riw-field select{color:#f1f5f9;}
      :root[data-theme="dark"] .riw-filter .riw-field input::placeholder{color:#94a3b8;opacity:1;}
      :root[data-theme="dark"] .riw-filter .riw-field svg{color:#94a3b8;}
      :root[data-theme="dark"] .riw-filter .riw-field select option{background:#1a1d26;color:#f1f5f9;}
      :root[data-theme="dark"] .riw-clear{background:transparent;border-color:#7f1d1d;color:#fca5a5;}
      :root[data-theme="dark"] .riw-clear:hover{background:rgba(127,29,29,.3);}
      :root[data-theme="dark"] .riw-count{color:#94a3b8;}
      :root[data-theme="dark"] .riw-count b{color:#f1f5f9;}
      :root[data-theme="dark"] .riw-card{background:#1a1d26;border-color:#2f333e;}
      :root[data-theme="dark"] .riw-card:hover{border-color:${palette.hover};box-shadow:0 4px 12px rgba(0,0,0,.4);}
      :root[data-theme="dark"] .riw-card-thumb{background:#0f1218;border-color:#2f333e;}
      :root[data-theme="dark"] .riw-card-thumb-ph{color:#64748b;}
      :root[data-theme="dark"] .riw-card-id{color:#e3bd5a;}
      :root[data-theme="dark"] .riw-card-title{color:#f1f5f9;}
      :root[data-theme="dark"] .riw-card-sub{color:#94a3b8;}
      :root[data-theme="dark"] .riw-card-sub b{color:#e2e8f0;}
      :root[data-theme="dark"] .riw-meta{color:#94a3b8;}
      :root[data-theme="dark"] .riw-meta b{color:#f1f5f9;}
      :root[data-theme="dark"] .riw-chip{background:rgba(148,163,184,.18);color:#cbd5e1;}
      :root[data-theme="dark"] .riw-chip-success{background:rgba(34,197,94,.2);color:#86efac;}
      :root[data-theme="dark"] .riw-chip-warn{background:rgba(250,204,21,.18);color:#fde68a;}
      :root[data-theme="dark"] .riw-chip-danger{background:rgba(239,68,68,.2);color:#fca5a5;}
      :root[data-theme="dark"] .riw-chip-neutral{background:rgba(148,163,184,.18);color:#cbd5e1;}
      :root[data-theme="dark"] .riw-empty{background:#1a1d26;border-color:#2f333e;color:#94a3b8;}

      @media (max-width: 480px){
        .riw-modal-overlay .riw-modal-card{padding:12px;max-height:96vh;}
        .riw-filter .riw-field{flex:1 1 100%;}
        .riw-card{padding:10px;}
        .riw-card-body{grid-template-columns:60px 1fr;gap:10px;}
        .riw-card-thumb{width:60px;height:60px;}
        .riw-card-title{font-size:13.5px;}
        .riw-meta{gap:4px 10px;font-size:11px;}
      }
    `}</style>
    <Inner>
      {!inline && <div className="riw-modal-head">
        <h3><History size={16} aria-hidden="true"/> {title}</h3>
        <button type="button" className="riw-close-btn" onClick={onClose} data-testid={`${testPrefix}-close`}>
          <X size={14} aria-hidden="true" style={{ verticalAlign: "-2px" }}/> Tutup
        </button>
      </div>}

      <div className="riw-filter" data-testid={`${testPrefix}-filter`}>
        <div className="riw-field">
          <Search size={14} aria-hidden="true"/>
          <input type="search" value={q} onChange={e=>setQ(e.target.value)}
            placeholder="Cari nomor pesanan / nota / pelanggan…"
            data-testid={`${testPrefix}-q`} aria-label="Pencarian riwayat"/>
        </div>
        {canFilterByPic && <div className="riw-field">
          <UsersIcon size={14} aria-hidden="true"/>
          <select value={picId} onChange={e=>setPicId(e.target.value)} data-testid={`${testPrefix}-pic`} aria-label="Filter Operator">
            <option value="">Semua Operator</option>
            {(pics || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>}
        <div className="riw-field">
          <Filter size={14} aria-hidden="true"/>
          <select value={status} onChange={e=>setStatus(e.target.value)} data-testid={`${testPrefix}-status`} aria-label="Filter status">
            <option value="">Semua status</option>
            <option value="BELUM_FIX">Belum Fix</option>
            <option value="REVISI">Revisi</option>
            <option value="FIX">Fix</option>
            <option value="BATAL">Batal</option>
            {flavor === "shopee" && <option value={SENT_24H}>Baru Dikirim (24 Jam)</option>}
          </select>
        </div>
        <div className="riw-field">
          <Calendar size={14} aria-hidden="true"/>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} data-testid={`${testPrefix}-from`} aria-label="Dari tanggal"/>
          <span style={{ fontSize: 12, color: "#64748b" }}>→</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} data-testid={`${testPrefix}-to`} aria-label="Sampai tanggal"/>
        </div>
        {filtersActive && <button type="button" className="riw-clear" onClick={clearFilters} data-testid={`${testPrefix}-clear`}>Hapus filter</button>}
      </div>

      <div className="riw-count" data-testid={`${testPrefix}-count`}>
        Menampilkan <b>{filtered.length}</b> pekerjaan{filtersActive ? " (sesuai filter)" : ""}.
        {!canFilterByPic && me?.name && <> · Operator: <b>{me.name}</b></>}
      </div>

      {filtered.length === 0 ? (
        <div className="riw-empty" data-testid={`${testPrefix}-empty`}>
          {status === SENT_24H && !q && !picId && !dateFrom && !dateTo
            ? "Belum ada pekerjaan yang dikirim dalam 24 jam terakhir."
            : "Tidak ada pekerjaan pada filter ini."}
        </div>
      ) : (
        <div className="riw-list" data-testid={`${testPrefix}-list`}>
          {filtered.map(it => <RiwayatCard key={it.id} it={it} onOpen={onOpen} fmtDT={fmtDT} testPrefix={testPrefix} onHoldDone={onHoldDone}/>)}
        </div>
      )}
    </Inner>
  </Wrapper>;
}

// Iter 360 — Export helper agar card bisa dipakai inline (contoh: tab Riwayat
//   operator Mockup Offline) tanpa membawa header filter sendiri. Konsumer
//   bertanggung jawab menyediakan filter & list wrapper. Self-contained style.
// Iter 364 — Slot `thumbSlot` opsional (ReactNode) → render horizontal row
//   dengan thumbnail di kiri (reuse MonitorThumb/komponen apapun yang di-pass).
//   Caller bertanggung jawab menyediakan onClick+stopPropagation jika thumb
//   boleh di-klik tanpa men-trigger card onOpen.
export function RiwayatPekerjaanCard({ item, onOpen, flavor = "offline", testPrefix = "riwayat-pekerjaan", thumbSlot = null }) {
  const it = item;
  const clickable = Boolean(onOpen);
  const fmtDT = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("id-ID", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta",
      });
    } catch { return ""; }
  };
  const fmtD = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("id-ID", {
        day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Jakarta",
      });
    } catch { return ""; }
  };

  // Iter 365 — Offline flavor REUSE markup Pemantauan Rido (`monitor-v3-*`).
  //   Styles sudah ada di App.css, tidak perlu CSS baru. Hanya mapping isi:
  //     - customer → mon-card-customer
  //     - order_number → mon-card-nota (prefix "Nota ")
  //     - job_label → monitor-v3-product
  //     - status (BELUM_FIX/REVISI/FIX/BATAL) → badge pill di head-row
  //     - "Proses sekarang" → "Masih di Mockup" / "Sudah ke Gudang" / "Dibatalkan"
  //     - "Oleh" → pic_name (ikon User)
  //     - "Update terakhir" → updated_at (ikon Calendar)
  //   Shopee flavor TIDAK diubah di sini (kartunya di-render dari modal).
  if (flavor === "offline") {
    const prosesText = it.is_cancelled
      ? "Dibatalkan"
      : (it.is_fix ? (it.next_label && it.next_label.toLowerCase().includes("gudang") ? "Sudah ke Gudang" : (it.next_label || "Sudah diteruskan"))
                   : (it.status === "REVISI" ? "Masih di Mockup (Revisi)" : "Masih di Mockup"));
    const updateTxt = fmtDT(it.updated_at) || fmtD(it.updated_at) || "—";
    const dsClass = it.is_cancelled ? "ds-canceled" : (it.is_fix ? "" : "");
    return (
      <article
        className={`monitor-v2-card monitor-v3-card ${dsClass}`}
        data-testid={`${testPrefix}-row-${it.order_number}`}
        data-status={it.status}
        onClick={clickable ? ((e) => {
          if (e.target && e.target.closest && e.target.closest("button, a, [role='button']")) return;
          onOpen(it);
        }) : undefined}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : -1}
        onKeyDown={clickable ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(it); } }) : undefined}
        aria-label={clickable ? `Buka detail ${it.order_number}` : undefined}
        style={clickable ? { cursor: "pointer" } : undefined}
      >
        <div className="monitor-v3-identity">
          {thumbSlot && <div className="monitor-v3-thumb-wrap" onClick={e=>e.stopPropagation()}>{thumbSlot}</div>}
          <div className="monitor-v3-ident-text">
            <div className="monitor-v3-head-row">
              <strong className="mon-card-customer" data-testid={`${testPrefix}-customer-${it.order_number}`}>{it.customer || it.order_number}</strong>
              <RiwayatPekerjaanBadge status={it.status}/>
            </div>
            <span className="mon-card-nota" data-testid={`${testPrefix}-nota-${it.order_number}`}>Nota {it.order_number}</span>
            {it.job_label && it.job_label !== "—" && (
              <p className="monitor-v3-product muted" style={{display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                <span>{it.job_label}</span>
                {/* Iter 536 — Badge jenis pengerjaan hanya untuk item dengan source
                    data pengerjaan (offline). Shopee items tidak set field ini → skip. */}
                {(it.process_mode !== undefined || it.order_type !== undefined || it.custom_type !== undefined) && (
                  <JenisPengerjaanBadge
                    product={{ process_mode: it.process_mode }}
                    order={{ order_type: it.order_type, custom_type: it.custom_type }}
                    size="xs"
                    testIdPrefix={`${testPrefix}-jenis-${it.order_number}`}
                  />
                )}
              </p>
            )}
          </div>
        </div>
        <div className="monitor-v3-details">
          <div className="monitor-v3-row monitor-v3-row-stack">
            <span className="monitor-v3-row-label">Proses sekarang</span>
            <span className="monitor-v3-process" data-testid={`${testPrefix}-proses-${it.order_number}`}>{prosesText}</span>
          </div>
          {it.pic_name && <div className="monitor-v3-row">
            <span className="monitor-v3-row-label" style={{display:"inline-flex",alignItems:"center",gap:4}}><UserIcon size={12} aria-hidden="true"/>Oleh:</span>
            <span className="monitor-v3-row-value" data-testid={`${testPrefix}-pic-${it.order_number}`}>{it.pic_name}</span>
          </div>}
          {updateTxt && updateTxt !== "—" && <div className="monitor-v3-row">
            <span className="monitor-v3-row-label">Update terakhir:</span>
            <span className="monitor-v3-row-value monitor-v3-deadline">
              <Calendar size={13} aria-hidden="true"/>
              <span data-testid={`${testPrefix}-updated-${it.order_number}`}>{updateTxt}</span>
            </span>
          </div>}
          {it.revision_count > 0 && <div className="monitor-v3-row">
            <span className="monitor-v3-row-label">Revisi:</span>
            <span className="monitor-v3-row-value" data-testid={`${testPrefix}-rev-${it.order_number}`}>{it.revision_count}×</span>
          </div>}
        </div>
        <div className="monitor-v3-foot">
          <button
            type="button"
            className="monitor-v3-detail-link"
            onClick={clickable ? ((e)=>{ e.stopPropagation(); onOpen(it); }) : undefined}
            data-testid={`${testPrefix}-detail-btn-${it.order_number}`}
            aria-label={`Lihat rincian ${it.order_number}`}
          >
            <span>Lihat rincian</span>
            <ChevronRight size={14} aria-hidden="true"/>
          </button>
        </div>
      </article>
    );
  }

  // ------- Shopee flavor (markup lama, tidak diubah) -------
  // Iter 363/364 — Palet Shopee tetap seperti sebelumnya (oranye, kartu horizontal).
  const palette = { card: "#fffbeb", cardBorder: "#fed7aa", hover: "#ea580c", accent: "#9a3412", chipRevBg: "#fde68a", chipRevC: "#92400e", title: "#1c1917", sub: "#64748b", meta: "#44403c", metaBold: "#1c1917" };
  const metaMasuk = fmtDT(it.created_at);
  const metaUpdate = fmtDT(it.updated_at);
  const hasSub = Boolean(it.customer || it.pic_name);
  return <>
    <style>{`
      .riw-ci{background:${palette.card};border:1px solid ${palette.cardBorder};border-radius:12px;padding:8px 10px;transition:border-color .15s ease, box-shadow .15s ease;${clickable ? "cursor:pointer;" : ""}}
      ${clickable ? `.riw-ci:hover{border-color:${palette.hover};box-shadow:0 2px 8px rgba(0,0,0,.18);}` : ""}
      .riw-ci-row{display:flex;gap:10px;align-items:flex-start;}
      .riw-ci-thumb-slot{flex:0 0 56px;display:flex;}
      .riw-ci-thumb-slot .monitor-v3-thumb{width:56px;height:56px;border-radius:8px;}
      .riw-ci-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;}
      .riw-ci-head{display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;}
      .riw-ci-id{font-family:'Space Mono',monospace;font-size:12.5px;font-weight:700;color:${palette.accent};}
      .riw-ci-badge{padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;display:inline-flex;align-items:center;gap:4px;line-height:1.2;}
      .riw-ci-title{font-size:13.5px;font-weight:700;color:${palette.title};margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
      .riw-ci-sub{font-size:12px;color:${palette.sub};margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
      .riw-ci-sub b{color:${palette.title};font-weight:700;}
      .riw-ci-chips{display:flex;flex-wrap:wrap;gap:4px;margin-top:3px;}
      .riw-ci-chip{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:600;padding:2px 7px;border-radius:999px;background:rgba(148,163,184,.18);color:${palette.meta};line-height:1.3;}
      .riw-ci-chip-success{background:rgba(34,197,94,.18);color:#86efac;}
      .riw-ci-chip-warn{background:${palette.chipRevBg};color:${palette.chipRevC};}
      .riw-ci-chip-danger{background:rgba(239,68,68,.18);color:#fca5a5;}
      .riw-ci-chip-neutral{background:rgba(148,163,184,.18);color:${palette.meta};}
      .riw-ci-meta{display:flex;flex-wrap:wrap;gap:2px 12px;font-size:11px;color:${palette.meta};margin-top:3px;}
      .riw-ci-meta b{color:${palette.metaBold};font-weight:700;}
      ${flavor === "offline" ? `
      :root:not([data-theme="dark"]) .riw-ci{background:#fff;border-color:#e2e8f0;}
      :root:not([data-theme="dark"]) .riw-ci-id{color:#9a7a1f;}
      :root:not([data-theme="dark"]) .riw-ci-title{color:#1c1917;}
      :root:not([data-theme="dark"]) .riw-ci-sub{color:#64748b;}
      :root:not([data-theme="dark"]) .riw-ci-sub b{color:#1c1917;}
      :root:not([data-theme="dark"]) .riw-ci-meta{color:#44403c;}
      :root:not([data-theme="dark"]) .riw-ci-meta b{color:#1c1917;}
      :root:not([data-theme="dark"]) .riw-ci-chip{background:#e2e8f0;color:#334155;}
      :root:not([data-theme="dark"]) .riw-ci-chip-success{background:#dcfce7;color:#166534;}
      :root:not([data-theme="dark"]) .riw-ci-chip-danger{background:#fee2e2;color:#991b1b;}
      :root:not([data-theme="dark"]) .riw-ci-chip-neutral{background:#e2e8f0;color:#334155;}
      ` : ""}
      @media (max-width: 480px){
        .riw-ci{padding:8px 10px;}
        .riw-ci-thumb-slot{flex:0 0 48px;}
        .riw-ci-thumb-slot .monitor-v3-thumb{width:48px;height:48px;}
        .riw-ci-title{font-size:13px;}
      }
    `}</style>
    <article
      className="riw-ci"
      data-testid={`${testPrefix}-row-${it.order_number}`}
      onClick={clickable ? ((e) => {
        // Jangan trigger onOpen bila klik dari elemen interaktif internal (thumb/button).
        if (e.target && e.target.closest && e.target.closest("button, a, [role='button']")) return;
        onOpen(it);
      }) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      onKeyDown={clickable ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(it); } }) : undefined}
      aria-label={clickable ? `Buka detail ${it.order_number}` : undefined}
    >
      <div className="riw-ci-row">
        {thumbSlot && <div className="riw-ci-thumb-slot" onClick={e=>e.stopPropagation()}>{thumbSlot}</div>}
        <div className="riw-ci-main">
          <div className="riw-ci-head">
            <span className="riw-ci-id">{it.order_number}</span>
            <RiwayatPekerjaanBadge status={it.status}/>
          </div>
          {it.job_label && it.job_label !== "—" && <p className="riw-ci-title">{it.job_label}</p>}
          {hasSub && <p className="riw-ci-sub">
            {it.customer ? <>Pelanggan: <b>{it.customer}</b></> : null}
            {it.customer && it.pic_name ? " · " : ""}
            {it.pic_name ? <>Operator: <b>{it.pic_name}</b></> : null}
          </p>}
          <div className="riw-ci-chips">
            {it.is_fix && <span className="riw-ci-chip riw-ci-chip-success" data-testid={`${testPrefix}-fix-${it.order_number}`}><CheckCircle2 size={11}/> Sudah Fix</span>}
            {!it.is_fix && !it.is_cancelled && <span className="riw-ci-chip">{it.status === "REVISI" ? "Masih Revisi" : "Belum Fix"}</span>}
            {it.revision_count > 0 && <span className="riw-ci-chip riw-ci-chip-warn" data-testid={`${testPrefix}-rev-${it.order_number}`}>
              <PenSquare size={11}/> Revisi {it.revision_count}×
            </span>}
            {it.next_label && it.next_label !== "—" && <span className={`riw-ci-chip ${(it.next_cls || "").replace("riw-chip", "riw-ci-chip") || "riw-ci-chip-neutral"}`} data-testid={`${testPrefix}-next-${it.order_number}`}>
              <WarehouseIcon size={11}/> {it.next_label}
            </span>}
            {it.is_cancelled && <span className="riw-ci-chip riw-ci-chip-danger" data-testid={`${testPrefix}-batal-${it.order_number}`}>
              <Ban size={11}/> Dibatalkan{it.cancel_reason ? `: ${it.cancel_reason}` : ""}
            </span>}
          </div>
          {(metaMasuk || metaUpdate) && <div className="riw-ci-meta">
            {metaMasuk && <span>Masuk <b>{metaMasuk}</b></span>}
            {metaUpdate && metaUpdate !== metaMasuk && <span>Update <b>{metaUpdate}</b></span>}
          </div>}
        </div>
      </div>
    </article>
  </>;
}

function RiwayatCard({ it, onOpen, fmtDT, testPrefix, onHoldDone }) {
  const clickable = Boolean(onOpen);
  // Iter 470 — Thumbnail state: load → ok|failed → placeholder.
  const [thumbState, setThumbState] = useState(it.thumb_url ? "loading" : "empty");
  // Iter 499 — Thumbnail CLICKABLE untuk buka Preview mockup, termasuk setelah
  //   job FIX / forwarded ke Gudang. Backend `/shopee/files/{id}/preview` sudah
  //   mengizinkan designer (PIC) view file miliknya kapan pun. Fallback: derive
  //   preview URL dari thumb URL kalau item lama belum ter-mapping `preview_url`.
  // Iter 514 — Jika `it.mockup_previews` (array) tersedia dan punya > 1 item,
  //   buka GALLERY. Fallback single-file untuk backward compat (offline).
  const galleryItems = Array.isArray(it.mockup_previews) ? it.mockup_previews : [];
  const useGallery = galleryItems.length > 1;
  const mockupCount = galleryItems.length || (it.preview_url ? 1 : 0);
  const previewUrl = it.preview_url
    || (it.thumb_url ? String(it.thumb_url).replace(/\/thumbnail(\?.*)?$/, "/preview") : "");
  const canPreview = Boolean(previewUrl) || useGallery;
  const [showPreview, setShowPreview] = useState(false);
  const onThumbClick = (e) => {
    e.stopPropagation();
    if (canPreview) setShowPreview(true);
  };
  return <>
  <article
    className="riw-card"
    data-testid={`${testPrefix}-row-${it.order_number}`}
    onClick={clickable ? ((e)=>{
      if (e.target && e.target.closest && e.target.closest("button, a, [role='button']")) return;
      onOpen(it);
    }) : undefined}
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : -1}
    onKeyDown={clickable ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(it); } }) : undefined}
    aria-label={clickable ? `Buka detail ${it.order_number}` : undefined}
  >
    <div className="riw-card-body">
      {/* Iter 470 — Thumbnail compact di sisi kiri (fallback placeholder bila tiada mockup).
          Iter 499 — Wrapped dengan role=button + onClick → buka modal preview.
          Role=button membuat article onClick skip (karena ada guard closest("[role='button']"))
          sehingga klik thumb tidak men-trigger onOpen kartu. */}
      <div className="riw-card-thumb" data-testid={`${testPrefix}-thumb-${it.order_number}`}
        role={canPreview ? "button" : undefined}
        tabIndex={canPreview ? 0 : -1}
        onClick={canPreview ? onThumbClick : undefined}
        onKeyDown={canPreview ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setShowPreview(true); } }) : undefined}
        aria-label={canPreview ? `Pratinjau mockup ${it.order_number}${mockupCount > 1 ? ` (${mockupCount} mockup)` : ""}` : undefined}
        title={canPreview ? (mockupCount > 1 ? `Klik untuk preview ${mockupCount} mockup` : "Klik untuk preview mockup") : undefined}
        style={canPreview ? { cursor: "pointer", position: "relative" } : { position: "relative" }}>
        {it.thumb_url && thumbState !== "failed" ? (
          <img src={it.thumb_url} alt="thumb" loading="lazy" decoding="async"
            onLoad={() => setThumbState("ok")}
            onError={() => setThumbState("failed")}
            style={{opacity: thumbState === "ok" ? 1 : 0.5}}/>
        ) : (
          <div className="riw-card-thumb-ph">
            <ImgIcon size={16} aria-hidden="true"/>
            <span>Belum ada mockup</span>
          </div>
        )}
        {/* Iter 514 — Badge "N Mockup" bila punya >1 mockup aktif. */}
        {mockupCount > 1 && (
          <span data-testid={`${testPrefix}-mockup-count-${it.order_number}`}
            style={{position:"absolute", bottom:3, right:3, background:"rgba(249,115,22,.95)",
              color:"#fff", borderRadius:999, padding:"1px 6px", fontSize:9.5, fontWeight:700,
              letterSpacing:0.2, lineHeight:1.4, boxShadow:"0 1px 2px rgba(0,0,0,.3)"}}>
            {mockupCount}×
          </span>
        )}
      </div>
      <div className="riw-card-main">
        <div className="riw-card-head">
          <span className="riw-card-id">{it.order_number}</span>
          <RiwayatPekerjaanBadge status={it.status}/>
          {/* Iter 541 — Kebab menu HOLD/Pembatalan di sudut card.
              Scope: Shopee only pada iterasi ini. Tombol menu di-stop event
              propagation agar klik tidak men-trigger onOpen card. Status held
              /canceled di-pass dari item (shopee_module.js menginject it.hold*).
              PIC yang pernah menangani boleh ajukan HOLD walau job sudah FIX
              atau diteruskan ke Gudang (backend _pic_ever_handled mencari lintas
              shopee_jobs + orders + audit log). */}
          {it.testPrefixForHold !== undefined || true ? (
            <span style={{ marginLeft: "auto" }}>
              <HoldKebabMenu
                orderNumber={it.order_number}
                customerName={it.customer}
                held={it.hold_active}
                canceled={it.cancellation_status === "canceled_internal"}
                menuId={`riwayat:${it.id}`}
                onDone={onHoldDone}
                testIdPrefix={`${testPrefix}-hold`}
              />
            </span>
          ) : null}
        </div>
        <p className="riw-card-title">{it.job_label || "—"}</p>
        <p className="riw-card-sub">
          {it.customer ? <>Pelanggan: <b>{it.customer}</b></> : null}
          {it.customer && it.pic_name ? " · " : ""}
          {it.pic_name ? <>Operator: <b>{it.pic_name}</b></> : null}
          {it.extra_label && <> · <span className="riw-chip" style={{ padding: "1px 6px" }}>{it.extra_label}</span></>}
        </p>
        <div className="riw-chips">
          {it.is_fix && <span className="riw-chip riw-chip-success" data-testid={`${testPrefix}-fix-${it.order_number}`}><CheckCircle2 size={11}/> Sudah Fix</span>}
          {!it.is_fix && !it.is_cancelled && <span className="riw-chip">{it.status === "REVISI" ? "Masih Revisi" : "Belum Fix"}</span>}
          {it.revision_count > 0 && <span className="riw-chip riw-chip-warn" data-testid={`${testPrefix}-rev-${it.order_number}`}>
            <PenSquare size={11}/> Revisi {it.revision_count}×
          </span>}
          {it.next_label && <span className={`riw-chip ${it.next_cls || "riw-chip-neutral"}`} data-testid={`${testPrefix}-next-${it.order_number}`}>
            <WarehouseIcon size={11}/> {it.next_label}
          </span>}
          {it.is_cancelled && <span className="riw-chip riw-chip-danger" data-testid={`${testPrefix}-batal-${it.order_number}`}>
            <Ban size={11}/> Dibatalkan{it.cancel_reason ? `: ${it.cancel_reason}` : ""}
          </span>}
        </div>
        <div className="riw-meta">
          <span>Masuk: <b>{fmtDT(it.created_at)}</b></span>
          <span>Update terakhir: <b>{fmtDT(it.updated_at)}</b></span>
          {it.fix_at && <span>FIX: <b>{fmtDT(it.fix_at)}</b></span>}
          {it.sent_at && <span>Ke Gudang: <b>{fmtDT(it.sent_at)}</b></span>}
        </div>
      </div>
    </div>
  </article>
  {showPreview && (useGallery || previewUrl) && (
    <RiwayatPreviewModal
      url={previewUrl}
      filename={it.preview_filename || it.job_label || it.order_number}
      isPdf={!!it.preview_is_pdf}
      items={useGallery ? galleryItems : null}
      orderNumber={it.order_number}
      customer={it.customer || ""}
      onClose={()=>setShowPreview(false)}
      testPrefix={testPrefix}/>
  )}
  </>;
}

// Iter 499 — Modal preview sederhana untuk Riwayat Pekerjaan. Tidak mengubah
//   workflow / tidak create file baru. Menampilkan file aktif terbaru via URL
//   endpoint `/preview` yang sudah ada (shopee_files/{id}/preview). Permission
//   di-handle backend: designer/PIC view miliknya kapan pun; Gudang view
//   selama state ∈ {sent, completed}; Bordir sesuai scope snapshot.
function RiwayatPreviewModal({ url, filename, isPdf, items, orderNumber, customer, onClose, testPrefix }) {
  // Iter 514 — Gallery mode: bila `items` (array dari semua mockup) diteruskan,
  //   tampilkan dengan navigasi prev/next + strip thumbnail. Fallback: single
  //   url (backward compat untuk Mockup Offline atau job dengan 1 mockup).
  const gallery = Array.isArray(items) && items.length > 1 ? items : null;
  const [idx, setIdx] = useState(0);
  const cur = gallery ? gallery[Math.min(idx, gallery.length - 1)] : null;
  const effUrl = cur ? cur.preview_url : url;
  const effFilename = cur ? (cur.filename || cur.title || filename) : filename;
  const effIsPdf = cur ? cur.is_pdf : isPdf;
  // Esc untuk tutup; panah ← → untuk navigasi gallery.
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [effUrl]);
  useEffect(() => {
    if (!gallery) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setIdx(i => (i - 1 + gallery.length) % gallery.length);
      else if (e.key === "ArrowRight") setIdx(i => (i + 1) % gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gallery]);
  return <div
    className="riw-preview-backdrop"
    role="dialog"
    aria-modal="true"
    aria-label={`Pratinjau mockup ${orderNumber}`}
    data-testid={`${testPrefix}-preview-${orderNumber}`}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(15,23,42,.78)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16,
    }}>
    <div className="riw-preview-inner" onClick={(e) => e.stopPropagation()}
      style={{
        background: "#0f1218", borderRadius: 14, maxWidth: 900, width: "100%",
        maxHeight: "92vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 50px rgba(0,0,0,.5)",
      }}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"10px 14px", borderBottom:"1px solid #1f2430", gap:8, flexShrink:0}}>
        <div style={{minWidth:0}}>
          <p style={{margin:0, fontSize:11, letterSpacing:1, color:"#8a94a7", fontWeight:700}}>
            MOCKUP{gallery ? ` · ${idx + 1} / ${gallery.length}` : ""}
          </p>
          <strong style={{fontSize:14, color:"#e9edf4", display:"block", overflow:"hidden",
            textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{customer || orderNumber}</strong>
          <span style={{fontSize:11.5, color:"#8a94a7"}}>{orderNumber}
            {cur?.title ? <> · {cur.title}</> : null}
          </span>
        </div>
        <button type="button"
          onClick={onClose}
          data-testid={`${testPrefix}-preview-close-${orderNumber}`}
          aria-label="Tutup pratinjau"
          style={{background:"#1f2430", color:"#e9edf4", border:"0", borderRadius:8,
            padding:"6px 12px", fontSize:13, fontWeight:600, cursor:"pointer", minHeight:36}}>
          Tutup
        </button>
      </div>
      <div style={{flex:1, overflow:"auto", background:"#0b0e14", position:"relative",
        display:"flex", alignItems:"center", justifyContent:"center", padding:12}}>
        {err ? (
          <div style={{color:"#f87171", fontSize:13, textAlign:"center", padding:20}}>
            Gagal memuat preview. File mungkin tidak lagi tersedia.
          </div>
        ) : effIsPdf ? (
          <iframe src={effUrl} title={effFilename} style={{width:"100%", height:"70vh", border:0, background:"#fff"}}/>
        ) : (
          <img src={effUrl} alt={effFilename}
            style={{maxWidth:"100%", maxHeight:"80vh", objectFit:"contain", background:"#fff", borderRadius:8}}
            onError={() => setErr(true)}
            data-testid={`${testPrefix}-preview-img-${orderNumber}`}/>
        )}
        {gallery && (
          <>
            <button type="button" onClick={() => setIdx(i => (i - 1 + gallery.length) % gallery.length)}
              aria-label="Mockup sebelumnya"
              data-testid={`${testPrefix}-preview-prev-${orderNumber}`}
              style={{position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
                background:"rgba(15,23,42,.72)", color:"#fff", border:"1px solid rgba(255,255,255,.2)",
                borderRadius:"50%", width:40, height:40, fontSize:20, fontWeight:700, cursor:"pointer"}}>‹</button>
            <button type="button" onClick={() => setIdx(i => (i + 1) % gallery.length)}
              aria-label="Mockup berikutnya"
              data-testid={`${testPrefix}-preview-next-${orderNumber}`}
              style={{position:"absolute", right:16, top:"50%", transform:"translateY(-50%)",
                background:"rgba(15,23,42,.72)", color:"#fff", border:"1px solid rgba(255,255,255,.2)",
                borderRadius:"50%", width:40, height:40, fontSize:20, fontWeight:700, cursor:"pointer"}}>›</button>
          </>
        )}
      </div>
      {gallery && (
        <div style={{display:"flex", gap:6, overflowX:"auto", padding:"8px 10px", background:"#0b0e14",
          borderTop:"1px solid #1f2430"}}
          data-testid={`${testPrefix}-preview-strip-${orderNumber}`}>
          {gallery.map((it, i) => (
            <button key={it.file_id} type="button" onClick={()=>setIdx(i)}
              data-testid={`${testPrefix}-preview-thumb-${i}`}
              aria-label={`Pilih mockup ${i + 1}`}
              style={{flexShrink:0, padding:0, border: i === idx ? "2px solid #f97316" : "2px solid transparent",
                borderRadius:6, cursor:"pointer", background:"#0f141c"}}>
              {it.is_pdf
                ? <div style={{width:56, height:56, display:"flex", alignItems:"center", justifyContent:"center",
                    color:"#f1f5f9", fontSize:10, fontWeight:700}}>PDF</div>
                : <img src={it.thumb_url || it.preview_url} alt={it.title}
                    style={{width:56, height:56, objectFit:"cover", borderRadius:4, display:"block"}}/>}
            </button>
          ))}
        </div>
      )}
    </div>
  </div>;
}
